import { memo, useRef, useState } from 'react';
import type { DemoScript, FingerKey, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, INK, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Era } from '../../art.js';
import { ERA_SKIN } from '../../art.js';
import { CLAY, GROUND, clamp, demoRound, feedbackTo, lerp, playDemo } from './garden.js';
import type { Feedback } from './garden.js';

/**
 * BIOTECHNICIAN · SORT THE SEEDS — tap left or right to drop each seed in its pot.
 *
 * Seeds fall one at a time from the sack at the top, down the middle of the potting bench. Each
 * pot wears a picture of the seed it takes; a tap anywhere on the left half sends the seed into
 * the left pot, the right half into the right, and a seedling comes up in the pot. The wrong pot is
 * the mistake (the seed hops out and comes again); a seed left to fall to the bench only comes
 * again. Round 1 sorts six peas and corn, slowly; round 2 seven, a little quicker; round 3 eight
 * beans and sunflower seeds, quicker still. (Sunflower seeds against look-alikes that differ only
 * by their stripes, the old round 3, are still drawn; no round deals them since 6 Oct 2026.)
 */

export type SeedKind = 'pea' | 'corn' | 'bean' | 'sunflower' | 'plain';

export interface SeedLevel {
  /** How long a seed takes to fall to the bench, in milliseconds. */
  fallMs: number;
  count: number;
  /** The two kinds, one to a pot. */
  kinds: readonly [SeedKind, SeedKind];
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const SEED_LEVELS: readonly SeedLevel[] = easier(
  [
    { fallMs: 3_400, count: 6, kinds: ['pea', 'corn'] },
    { fallMs: 2_700, count: 8, kinds: ['bean', 'sunflower'] },
  ],
);

/**
 * The order the seeds come in, as pot numbers (0 or 1): as many of one as the other (give or
 * take one), and never more than three of a kind in a row, so a guess can't carry a round.
 */
export function seedRun(rand: () => number, count: number): (0 | 1)[] {
  const base = Array.from({ length: count }, (_, i) => (i % 2) as 0 | 1);
  for (let tries = 0; tries < 60; tries += 1) {
    const a = base.slice();
    for (let i = a.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    if (longestRun(a) <= 3) return a;
  }
  return base;
}

export function longestRun(xs: readonly number[]): number {
  let best = 0;
  let run = 0;
  xs.forEach((x, i) => {
    run = i > 0 && xs[i - 1] === x ? run + 1 : 1;
    best = Math.max(best, run);
  });
  return best;
}

/** Which pot a tap at `fx` (0–1 across the board) sends the seed to; the middle strip does nothing. */
export function potFor(fx: number): 0 | 1 | null {
  if (Math.abs(fx - 0.5) < 0.07) return null;
  return fx < 0.5 ? 0 : 1;
}

// The bench, in board units.
const W = BOARD.w;
const H = BOARD.h;
const MID = W / 2;
const TOP_Y = 56;
const BENCH_Y = 206;
const POT_X = [78, W - 78] as const;
const POT_Y = 216;
const FLY_MS = 380;
const BOUNCE_MS = 520;
const DROP_MS = 650;
const GAP_MS = 260;
/** The first seed comes this long after the round begins. */
const FIRST_MS = 700;
/** "Wrong pot" shows this long, and the pot shakes for the first part of it. */
const WRONG_MS = 700;
const SHAKE_MS = 420;
/** A new seedling comes up over this long. */
const GROW_MS = 450;

type Phase = 'wait' | 'fall' | 'fly' | 'bounce' | 'drop';

/** Everything the bench shows. */
export interface SeedsView {
  /** The game clock, ms. */
  t: number;
  /** The seed each pot takes, left and right. */
  k: readonly [SeedKind, SeedKind];
  /** Seedlings in each pot, and the newest while it comes up: its pot, and ms since. */
  sp: readonly [number, number];
  ng: readonly [pot: number, age: number] | null;
  /** The seed on its way: across, down (units), turn (degrees), how solid (0–1), and which pot's seed it is. */
  s: readonly [x: number, y: number, rot: number, o: number, pot: number] | null;
  /** Seeds sorted, and how many in all. */
  n: number;
  of: number;
  /** The pot a seed hopped out of, and ms since, while it shakes. */
  w: readonly [pot: number, age: number] | null;
  /** 0 sorting, 1 the wrong pot, 2 left to fall, 3 all sorted. */
  st: 0 | 1 | 2 | 3;
  fx?: Fx | undefined;
}

/**
 * A round of sorting, driven by taps (a guest's or the how-to's) and the game clock. `hand` deals
 * the pots and the order by hand (the how-to); otherwise they are dealt fresh each round.
 */
export function seedsRound(level: Level, rand: () => number, fb: Feedback, hand?: { pots: readonly [SeedKind, SeedKind]; run: readonly (0 | 1)[] }) {
  const L = SEED_LEVELS[level]!;
  // Which kind goes left is dealt fresh each round.
  const pots: readonly [SeedKind, SeedKind] = hand?.pots ?? (rand() < 0.5 ? L.kinds : [L.kinds[1], L.kinds[0]]);
  const run = hand?.run ?? seedRun(rand, L.count);
  let phase: Phase = 'wait';
  let at = 0;
  let startAt: number | null = null;
  let nextAt = 0;
  let spawnAt = 0;
  let dropAt = -1e9;
  let fly: { fromY: number; pot: 0 | 1; ok: boolean; at: number } | null = null;
  let wrong: { pot: 0 | 1; at: number } | null = null;
  let grew: { pot: 0 | 1; at: number } | null = null;
  const sprouts: [number, number] = [0, 0];
  let won = false;

  const fallY = (now: number) => lerp(TOP_Y, BENCH_Y - 10, clamp((now - spawnAt) / L.fallMs));

  /** Where the seed is drawn on its way, or null while none is. */
  const pose = (now: number): readonly [number, number, number, number] | null => {
    if (phase === 'fall') return [MID, fallY(now), Math.sin((now - spawnAt) / 380) * 14, 1];
    if (phase === 'fly' && fly) {
      const u = clamp((now - fly.at) / FLY_MS);
      const e = 1 - (1 - u) * (1 - u);
      return [lerp(MID, POT_X[fly.pot], e), lerp(fly.fromY, POT_Y - 4, e) - Math.sin(u * Math.PI) * 34, u * (fly.pot === 0 ? -260 : 260), 1];
    }
    if (phase === 'bounce' && fly) {
      const u = clamp((now - fly.at) / BOUNCE_MS);
      const out = fly.pot === 0 ? -1 : 1;
      return [POT_X[fly.pot] + out * u * 50, POT_Y - 4 - Math.sin(u * Math.PI) * 44 + u * u * 50, u * out * 400, 1 - u * u];
    }
    if (phase === 'drop') {
      const u = clamp((now - dropAt) / DROP_MS);
      return [MID + u * 16, BENCH_Y - 10 + Math.min(u * 3, 1) * 22, u * 200, 1 - u];
    }
    return null;
  };

  return {
    input: {
      down(p: Pt, now: number) {
        if (won || phase !== 'fall') return;
        const pot = potFor(p.x / W);
        if (pot === null) return;
        const ok = pot === run[at];
        fly = { fromY: fallY(now), pot, ok, at: now };
        phase = 'fly';
        if (ok) fb.progress((at + 1) / run.length);
        else {
          wrong = { pot, at: now };
          fb.miss();
        }
      },
      move() {},
      up() {},
    },
    step(now: number) {
      if (startAt === null) {
        startAt = now;
        nextAt = now + FIRST_MS;
      }
      if (won) return;
      if (phase === 'wait' && now >= nextAt) {
        phase = 'fall';
        spawnAt = now;
      } else if (phase === 'fall' && now - spawnAt >= L.fallMs) {
        // Nobody sorted it: it drops to the bench, and comes again.
        phase = 'drop';
        dropAt = now;
      } else if (phase === 'drop' && now - dropAt >= DROP_MS) {
        phase = 'wait';
        nextAt = now + GAP_MS;
      } else if (phase === 'fly' && fly && now - fly.at >= FLY_MS) {
        if (fly.ok) {
          sprouts[fly.pot] += 1;
          grew = { pot: fly.pot, at: now };
          fb.hit();
          at += 1;
          fly = null;
          if (at >= run.length) {
            won = true;
            fb.win();
          } else {
            phase = 'wait';
            nextAt = now + GAP_MS;
          }
        } else {
          phase = 'bounce';
          fly.at = now;
        }
      } else if (phase === 'bounce' && fly && now - fly.at >= BOUNCE_MS) {
        fly = null;
        phase = 'wait';
        nextAt = now + GAP_MS;
      }
    },
    view(now: number, fx: Fx | undefined): SeedsView {
      const s = won ? null : pose(now);
      const want = run[at];
      return {
        t: q(now),
        k: pots,
        sp: [sprouts[0], sprouts[1]],
        ng: grew && now - grew.at < GROW_MS ? [grew.pot, q(now - grew.at)] : null,
        s: s && want !== undefined ? [q(s[0]), q(s[1]), q(s[2]), q(s[3], 2), want] : null,
        n: at,
        of: run.length,
        w: wrong && now - wrong.at < SHAKE_MS ? [wrong.pot, q(now - wrong.at)] : null,
        st: won ? 3 : wrong && now - wrong.at < WRONG_MS ? 1 : phase === 'drop' ? 2 : 0,
        fx,
      };
    },
  };
}

/** A seedling coming up: it grows past its size and settles, `age` ms in. */
const sproutScale = (age: number) => {
  const u = clamp(age / GROW_MS);
  return u < 0.7 ? 1.12 * (1 - (1 - u / 0.7) ** 2) : 1.12 - 0.12 * ((u - 0.7) / 0.3);
};

/** The bench, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<SeedsView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  // A demo glides every number between its keys: counts and states are read rounded.
  const st = Math.round(view.st);
  const seed = view.s;
  const status =
    st === 3
      ? { text: say(lang, '✓ ALL SORTED', '✓ ALT SORTERT'), tone: 'ok' as const }
      : st === 1
        ? { text: say(lang, 'WRONG POT', 'FEIL POTTE'), tone: 'bad' as const }
        : st === 2
          ? { text: say(lang, 'TOO SLOW — AGAIN', 'FOR SEINT – IGJEN'), tone: 'warn' as const }
          : { text: say(lang, 'SEEDS', 'FRØ'), count: [Math.round(view.n), view.of] as const, hint: say(lang, 'TAP THE SIDE OF ITS POT', 'TRYKK PÅ POTTENS SIDE') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <SeedDefs id={id} />
        <radialGradient id={`${id}spot`} cx=".5" cy=".35" r=".6">
          <stop offset="0" stopColor={skin.haze} stopOpacity=".2" />
          <stop offset="1" stopColor={skin.haze} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}pot`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={CLAY.shade} />
          <stop offset=".35" stopColor={CLAY.lit} />
          <stop offset=".7" stopColor={CLAY.body} />
          <stop offset="1" stopColor={CLAY.deep} />
        </linearGradient>
        <linearGradient id={`${id}wood`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#8a5a34" />
          <stop offset="1" stopColor="#4a2e18" />
        </linearGradient>
      </defs>

      <WallArt era={era} />
      <ellipse cx={MID} cy="124" rx="80" ry="120" fill={`url(#${id}spot)`} />

      {/* the two halves: which way a tap sends the seed */}
      <g fill={accent} opacity=".42">
        <path d="M30 120 L46 108 L46 132 Z" />
        <path d={`M${W - 30} 120 L${W - 46} 108 L${W - 46} 132 Z`} />
      </g>
      <line x1={MID} y1={TOP_Y + 16} x2={MID} y2={BENCH_Y - 18} stroke={INK.hi} strokeOpacity=".07" strokeWidth="1" strokeDasharray="3 5" />

      {/* the sack the seeds come from */}
      <g transform={`translate(${MID} 4)`}>
        <path d="M0 -4 L0 6" stroke="#6b5234" strokeWidth="2" />
        <path d="M-22 6 Q -26 26 -12 38 L12 38 Q 26 26 22 6 Q 0 0 -22 6 Z" fill="#b08a58" stroke="#5e4424" strokeWidth="1" />
        <path d="M-12 38 Q 0 43 12 38 L8 44 L-8 44 Z" fill="#8a6a40" stroke="#5e4424" strokeWidth="1" />
        <path d="M-16 14 Q -10 22 -12 32 M-4 12 L-4 34 M8 14 Q 12 24 10 34" fill="none" stroke="#5e4424" strokeOpacity=".4" strokeWidth=".8" />
        <path d="M-18 9 Q -6 5 6 8" fill="none" stroke="#f4dcb0" strokeOpacity=".5" strokeWidth="1.4" strokeLinecap="round" />
      </g>

      {/* the bench */}
      <rect x="0" y={BENCH_Y + 26} width={W} height={H - BENCH_Y - 26} fill={`url(#${id}wood)`} />
      <rect x="0" y={BENCH_Y + 22} width={W} height="8" fill="#a8744a" />
      <line x1="0" y1={BENCH_Y + 22.5} x2={W} y2={BENCH_Y + 22.5} stroke="#f0c890" strokeOpacity=".45" />
      {[56, 146, 236].map((x) => (
        <line key={x} x1={x} y1={BENCH_Y + 30} x2={x + 20} y2={H} stroke="#2e1c0e" strokeOpacity=".35" />
      ))}

      {/* the pots */}
      {POT_X.map((x, i) => {
        const age = view.w && Math.round(view.w[0]) === i ? view.w[1] : null;
        const shake = age !== null && age < SHAKE_MS ? Math.sin(age / 30) * 4 * (1 - age / SHAKE_MS) : 0;
        const growing = view.ng && Math.round(view.ng[0]) === i ? sproutScale(view.ng[1]) : 1;
        return (
          <g key={i} transform={`translate(${(x + shake).toFixed(1)} ${POT_Y})`}>
            <Pot id={id} kind={view.k[i]!} sprouts={Math.round(view.sp[i]!)} growing={growing} era={era} />
          </g>
        );
      })}

      {/* the seed */}
      {seed && (
        <g transform={`translate(${seed[0].toFixed(1)} ${seed[1].toFixed(1)}) rotate(${seed[2].toFixed(1)})`} opacity={clamp(seed[3]).toFixed(2)}>
          <g transform="scale(1.6)">
            <SeedArt kind={view.k[Math.round(seed[4])] ?? view.k[0]} id={id} />
          </g>
        </g>
      )}
    </BoardFrame>
  );
}

/** The wall behind the bench, by era. */
function Wall({ era }: { era: Era }) {
  const skin = ERA_SKIN[era];
  const g = GROUND[era];
  return (
    <g>
      <rect x="0" y="0" width={W} height={BENCH_Y + 30} fill={skin.bg} />
      {era === 'stars' &&
        // A potting shed: boards.
        Array.from({ length: 9 }, (_, i) => (
          <g key={i}>
            <rect x={i * 36} y="0" width="35" height={BENCH_Y + 30} fill="#1a1712" opacity={i % 2 ? 0.55 : 0.4} />
            <line x1={i * 36 + 35.5} y1="0" x2={i * 36 + 35.5} y2={BENCH_Y + 30} stroke="#000" strokeOpacity=".5" />
          </g>
        ))}
      {era === 'neon' && (
        // A grow lab: tiles and a grow light.
        <g>
          {Array.from({ length: 8 }, (_, i) => (
            <line key={`v${i}`} x1={i * 42 + 16} y1="0" x2={i * 42 + 16} y2={BENCH_Y + 30} stroke={g.line} strokeOpacity=".08" />
          ))}
          {Array.from({ length: 6 }, (_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 42 + 20} x2={W} y2={i * 42 + 20} stroke={g.line} strokeOpacity=".08" />
          ))}
          <rect x="20" y="10" width="86" height="5" rx="2.5" fill={skin.ink} opacity=".55" />
          <rect x={W - 106} y="10" width="86" height="5" rx="2.5" fill={skin.ink} opacity=".55" />
        </g>
      )}
      {era === 'debris' && (
        // A salvage room: plates and rivets.
        <g>
          {[0, 1, 2].map((r) =>
            [0, 1, 2, 3].map((c) => (
              <rect key={`${r}${c}`} x={c * 80 + 2} y={r * 78 + 2} width="76" height="74" fill="#2a1414" opacity=".6" stroke="#000" strokeOpacity=".5" />
            )),
          )}
          {[0, 1, 2, 3].map((c) => (
            <circle key={c} cx={c * 80 + 8} cy="8" r="1.6" fill={g.line} opacity=".3" />
          ))}
        </g>
      )}
      {era === 'dust' &&
        // Sandstone blocks.
        [0, 1, 2, 3, 4].map((r) =>
          [0, 1, 2, 3, 4].map((c) => (
            <rect key={`${r}${c}`} x={c * 76 - (r % 2) * 38 + 1} y={r * 56 + 1} width="74" height="54" rx="3" fill="#3a2a16" opacity=".55" stroke="#000" strokeOpacity=".35" />
          )),
        )}
      {era === 'canopy' && (
        // A glasshouse: panes, and leaves beyond them.
        <g>
          {[40, 110, 196, 272].map((x, i) => (
            <ellipse key={x} cx={x} cy={44 + (i % 2) * 60} rx="34" ry="16" fill={g.leaf} opacity=".12" transform={`rotate(${i % 2 ? 20 : -25} ${x} ${44 + (i % 2) * 60})`} />
          ))}
          {Array.from({ length: 5 }, (_, i) => (
            <line key={i} x1={i * 68 + 24} y1="0" x2={i * 68 + 24} y2={BENCH_Y + 30} stroke={g.leafHi} strokeOpacity=".14" strokeWidth="2" />
          ))}
          <line x1="0" y1="104" x2={W} y2="104" stroke={g.leafHi} strokeOpacity=".14" strokeWidth="2" />
        </g>
      )}
    </g>
  );
}

/** A clay pot, its label showing the seed it takes, and a seedling for every seed sorted into it. */
function Pot({ id, kind, sprouts, growing, era }: { id: string; kind: SeedKind; sprouts: number; growing: number; era: Era }) {
  const g = GROUND[era];
  return (
    <g>
      <ellipse cx="0" cy="66" rx="46" ry="6" fill="#000" opacity=".4" />
      <path d="M-44 8 L44 8 L34 64 Q 33 68 28 68 L-28 68 Q -33 68 -34 64 Z" fill={`url(#${id}pot)`} stroke={CLAY.edge} strokeWidth="1" />
      <rect x="-50" y="-4" width="100" height="14" rx="3" fill={CLAY.rim} stroke={CLAY.edge} strokeWidth="1" />
      <line x1="-46" y1="-1" x2="40" y2="-1" stroke={CLAY.shine} strokeOpacity=".5" strokeWidth="1.4" strokeLinecap="round" />
      <ellipse cx="0" cy="-3" rx="46" ry="5" fill={CLAY.earth} />
      {Array.from({ length: sprouts }, (_, i) => {
        const x = -36 + ((i * 29) % 72);
        const s = i === sprouts - 1 ? growing : 1;
        return (
          <g key={i} transform={`translate(${x} -4) scale(${s.toFixed(3)})`}>
            <path d="M0 0 Q -1 -7 0 -12" stroke={g.stem} strokeWidth="1.6" fill="none" />
            <path d="M0 -11 C -3 -15, -8 -15, -9 -12 C -6 -10, -3 -10, 0 -11 Z" fill={g.leaf} />
            <path d="M0 -11 C 3 -15, 8 -15, 9 -12 C 6 -10, 3 -10, 0 -11 Z" fill={g.leafHi} />
          </g>
        );
      })}
      {/* the label */}
      <circle cx="0" cy="36" r="21" fill="#f3ead6" stroke="#8a6a4a" strokeWidth="1.2" />
      <circle cx="0" cy="36" r="17.5" fill="none" stroke="#8a6a4a" strokeOpacity=".35" strokeWidth=".8" />
      <g transform="translate(0 36) scale(1.05)">
        <SeedArt kind={kind} id={id} />
      </g>
    </g>
  );
}

function SeedDefs({ id }: { id: string }) {
  const grad = (name: string, hi: string, lo: string) => (
    <radialGradient id={`${id}${name}`} cx=".38" cy=".32" r=".75">
      <stop offset="0" stopColor={hi} />
      <stop offset="1" stopColor={lo} />
    </radialGradient>
  );
  return (
    <>
      {grad('pea', '#d8ffaa', '#3f8f24')}
      {grad('corn', '#fff6b0', '#e09a0c')}
      {grad('bean', '#ff8a8e', '#7a1420')}
      {grad('dark', '#6e6258', '#231d18')}
    </>
  );
}

/** One seed, about 24 units, drawn round its middle. The sunflower seed and the plain one differ only by the stripes. */
export function SeedArt({ kind, id }: { kind: SeedKind; id: string }) {
  const TEAR = 'M0 -13 C 8 -7, 9.5 6, 0 12 C -9.5 6, -8 -7, 0 -13 Z';
  switch (kind) {
    case 'pea':
      return (
        <g>
          <circle r="10" fill={`url(#${id}pea)`} stroke="#23511a" strokeWidth=".8" />
          <ellipse cx="-3.4" cy="-3.6" rx="3" ry="2" fill="#ffffff" opacity=".55" />
          <path d="M2 6 Q 4 5 5 3" fill="none" stroke="#23511a" strokeWidth="1" strokeLinecap="round" />
        </g>
      );
    case 'corn':
      return (
        <g>
          <path d="M-10 -9 Q 0 -14 10 -9 L7.5 7 Q 0 13 -7.5 7 Z" fill={`url(#${id}corn)`} stroke="#8a5a00" strokeWidth=".8" strokeLinejoin="round" />
          <path d="M-6 -8 Q 0 -10.5 6 -8 L5 -3 Q 0 -1 -5 -3 Z" fill="#fffbe0" opacity=".75" />
        </g>
      );
    case 'bean':
      return (
        <g>
          <path d="M-12 -1 C -12 -9, -2 -11, 3 -7 C 6 -5, 9 -8, 12 -4 C 14 4, 5 10, -3 9 C -10 8, -12 5, -12 -1 Z" fill={`url(#${id}bean)`} stroke="#4a0a12" strokeWidth=".8" />
          <ellipse cx="4.5" cy="-4" rx="2.4" ry="1.2" fill="#fff2e8" transform="rotate(20 4.5 -4)" />
          <ellipse cx="-6" cy="-3" rx="3" ry="1.5" fill="#ffffff" opacity=".45" />
        </g>
      );
    case 'sunflower':
      return (
        <g>
          <path d={TEAR} fill={`url(#${id}dark)`} stroke="#f0e6d0" strokeOpacity=".7" strokeWidth=".9" />
          <path d="M0 -11 L0 10.5" stroke="#f3ead6" strokeWidth="2" strokeLinecap="round" />
          <path d="M-4.2 -6 Q -5.6 2 -3.6 8.5" fill="none" stroke="#f3ead6" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M4.2 -6 Q 5.6 2 3.6 8.5" fill="none" stroke="#f3ead6" strokeWidth="1.8" strokeLinecap="round" />
        </g>
      );
    case 'plain':
      return (
        <g>
          <path d={TEAR} fill={`url(#${id}dark)`} stroke="#f0e6d0" strokeOpacity=".7" strokeWidth=".9" />
          <ellipse cx="-3" cy="-3" rx="1.6" ry="4" fill="#ffffff" opacity=".22" />
        </g>
      );
  }
}

// The wall stays the same from frame to frame: drawn once, not sixty times a second.
const WallArt = memo(Wall);

function Board(props: RoundProps) {
  const { level, lang } = props;
  const fb = useRef<Feedback | null>(null);
  const [game] = useState(() => seedsRound(level, seeded(Date.now() + level * 7919), feedbackTo(fb)));
  const round = useRound(props, (_dt, now) => game.step(now));
  fb.current = round;
  return (
    <View
      view={round.show(game.view(round.now, round.fx))}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap left or right', 'Trykk venstre eller høyre'), down: (p) => round.live && game.input.down(p, round.time()) }}
    />
  );
}

/**
 * The how-to, sorted by the rules themselves: round 1's peas and corn at round 1's pace. A corn
 * seed falls and the finger taps the right, where the corn pot is; then a pea, and a tap on the
 * left; then corn again.
 */
function demoSort(): DemoScript<SeedsView> {
  const ms = 6_400;
  const finger: FingerKey[] = [
    { at: 0, x: 250, y: 300, hide: true },
    { at: 400, x: 250, y: 300, hide: false },
    { at: 1_050, x: 250, y: 150 },
    { at: 1_500, x: 250, y: 150, down: true },
    { at: 1_620, x: 250, y: 150, down: false },
    { at: 2_450, x: 70, y: 150 },
    { at: 2_900, x: 70, y: 150, down: true },
    { at: 3_020, x: 70, y: 150, down: false },
    { at: 3_850, x: 250, y: 150 },
    { at: 4_300, x: 250, y: 150, down: true },
    { at: 4_420, x: 250, y: 150, down: false },
    { at: 5_300, x: 262, y: 300, hide: true },
  ];
  const round = demoRound();
  const game = seedsRound(0, seeded(1), round, { pots: ['pea', 'corn'], run: [1, 0, 1, 0, 1, 0] });
  const keys = playDemo(
    ms,
    finger,
    round,
    { input: game.input, step: (_dt, now) => game.step(now), view: (now) => game.view(now, round.fx()) },
    ({ t: _t, s, ng, w, fx: _fx, ...parts }) => ({ ...parts, s: s ? s[4] : null, ng: ng ? ng[0] : null, w: w ? w[0] : null }),
  );
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'A SEED FALLS', no: 'ET FRØ FALLER' },
      { at: 700, en: 'FIND THE POT WITH ITS PICTURE', no: 'FINN POTTEN MED BILDET AV DET' },
      { at: 1_300, en: 'TAP THAT SIDE', no: 'TRYKK PÅ DEN SIDEN' },
    ],
  };
}

export const game = thirtyGame<SeedsView>({
  Board,
  View,
  demo: demoSort(),
  words: {
    en: {
      instruction: 'Tap left or right to drop each seed in the pot that shows it. Each round quicker.',
      howMove: 'Look at the falling seed, then tap the side of the board where its pot is.',
    },
    no: {
      instruction: 'Trykk venstre eller høyre for å slippe hvert frø i potten som viser det.',
      howMove: 'Se på frøet som faller, og trykk på den siden av brettet der potten står.',
    },
  },
});
